import React, { useState, useRef, useEffect } from 'react';
import './WorkspaceTreeNav.css';

const WorkspaceTreeNav = ({
  userId,
  onFilterByTask,
  toggleStackExpansion,
  handleFilterByProject,
  handleFilterByWorkspace,
  handleFilterByJob
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedWorkspace, setExpandedWorkspace] = useState(null);
  const [expandedProject, setExpandedProject] = useState(null);
  const [expandedJob, setExpandedJob] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);

  // Store projects, jobs, and tasks data
  const [projectsData, setProjectsData] = useState({});
  const [jobsData, setJobsData] = useState({});
  const [tasksData, setTasksData] = useState({});

  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api`;

  const getLastActivityTimestamp = (item) => {
    const activityValue =
      item.lastActivity ??
      item.lastActivityDate ??
      item.activityDate ??
      item.updatedAt ??
      item.updatedDate ??
      item.modifiedAt ??
      item.modifiedDate ??
      item.createdAt ??
      item.createdDate;

    if (!activityValue) {
      return 0;
    }

    const parsed = new Date(activityValue).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const sortByLastActivityDesc = (items, idKey) =>
    [...items].sort((a, b) => {
      const activityDiff = getLastActivityTimestamp(b) - getLastActivityTimestamp(a);
      if (activityDiff !== 0) {
        return activityDiff;
      }
      return (Number(b[idKey]) || 0) - (Number(a[idKey]) || 0);
    });

  // Fetch entire navigation tree in one API call
  const fetchNavigationTree = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/Tree/GetNavigationTree/${userId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.message === "Navigation tree retrieved successfully" && data.data && data.data.workspaces) {
        // Transform workspaces
        const workspacesList = data.data.workspaces.map(workspace => ({
          id: workspace.workspaceId,
          name: workspace.workspaceName,
          projectCount: workspace.projectCount || 0
        }));
        
        setWorkspaces(workspacesList);
        
        // Transform and set projects data
        const projectsMap = {};
        const jobsMap = {};
        const tasksMap = {};
        
        data.data.workspaces.forEach(workspace => {
          if (workspace.projects) {
            projectsMap[workspace.workspaceId] = workspace.projects.map(project => ({
              id: project.projectId,
              name: project.projectName,
              workspaceId: workspace.workspaceId,
              jobCount: project.jobCount || 0
            }));
            
            workspace.projects.forEach(project => {
              if (project.jobs) {
                jobsMap[project.projectId] = sortByLastActivityDesc(
                  project.jobs.map(job => ({
                    id: job.jobId,
                    name: job.jobName,
                    projectId: project.projectId,
                    taskCount: job.taskCount || 0,
                    // Note: The API response doesn't include activity dates, so we'll use default sorting
                  })),
                  "id"
                );
                
                project.jobs.forEach(job => {
                  if (job.tasks) {
                    tasksMap[job.jobId] = sortByLastActivityDesc(
                      job.tasks.map(task => ({
                        id: task.taskId,
                        name: task.taskTitle,
                        jobId: job.jobId,
                        friendlyId: task.friendlyId,
                        noteCount: task.noteCount || 0,
                        startDate: task.startDate,
                        // Note: The API response doesn't include activity dates, so we'll use default sorting
                      })),
                      "id"
                    );
                  }
                });
              }
            });
          }
        });
        
        setProjectsData(projectsMap);
        setJobsData(jobsMap);
        setTasksData(tasksMap);
      } else {
        setWorkspaces([]);
        setProjectsData({});
        setJobsData({});
        setTasksData({});
        setError("No workspaces found");
      }
    } catch (err) {
      console.error("Error fetching navigation tree:", err);
      setError("Failed to load navigation tree");
      setWorkspaces([]);
      setProjectsData({});
      setJobsData({});
      setTasksData({});
    } finally {
      setLoading(false);
    }
  };







  // Handle workspace click - toggle expansion (projects are already loaded)
  const handleWorkspaceClick = (workspaceId) => {
    if (expandedWorkspace === workspaceId) {
      // Collapse if already expanded
      setExpandedWorkspace(null);
      setExpandedProject(null);
      setExpandedJob(null);
    } else {
      // Expand (projects are already loaded from the initial API call)
      setExpandedWorkspace(workspaceId);
      setExpandedProject(null);
      setExpandedJob(null);
    }
  };

  // Handle workspace double-click
  const handleWorkspaceDoubleClick = (e, workspace) => {
    e.stopPropagation(); // Prevent expanding the stack
    if (workspace.id && handleFilterByWorkspace) {
      handleFilterByWorkspace(workspace.id, workspace.name);
    }
  };

  // Handle project click - toggle expansion (jobs are already loaded)
  const handleProjectClick = (projectId) => {
    if (expandedProject === projectId) {
      // Collapse if already expanded
      setExpandedProject(null);
      setExpandedJob(null);
    } else {
      // Expand (jobs are already loaded from the initial API call)
      setExpandedProject(projectId);
      setExpandedJob(null);
    }
  };

  // Handle project double-click
  const handleProjectDoubleClick = (e, project) => {
    e.stopPropagation(); // Prevent expanding the stack
    if (project.id && handleFilterByProject) {
      handleFilterByProject(project.id, project.name);
    }
  };

  // Handle job click - toggle expansion (tasks are already loaded)
  const handleJobClick = (jobId) => {
    if (expandedJob === jobId) {
      // Collapse if already expanded
      setExpandedJob(null);
    } else {
      // Expand (tasks are already loaded from the initial API call)
      setExpandedJob(jobId);
    }
  };

  // Handle job double-click
  const handleJobDoubleClick = (e, job) => {
   e.stopPropagation(); // Prevent expanding the stack
    if (job.id && handleFilterByProject) {
      handleFilterByJob(job.id, job.name);
    }
  };

  // Handle task click
  const handleTaskClick = async (task, job, project, workspace) => {
    console.log('Selected task:', task, 'for job:', job);
    
    // Find the complete job object with all necessary data
    const completeJob = {
      jobId: job.id,
      jobName: job.name,
      workspaceId: workspace?.id,
      workspaceName: workspace?.name,
      projectId: project?.id,
      projectName: project?.name,
    };
    
    // Call the callback with task and job info
    if (onFilterByTask) {
      onFilterByTask(task.id, task.name, completeJob);
    }
    
    // Close the dropdown
    setIsOpen(false);
    
    // Reset expanded states
    setExpandedWorkspace(null);
    setExpandedProject(null);
    setExpandedJob(null);
  };

  // Helper function to get workspace by ID
  const getWorkspaceById = (workspaceId) => {
    return workspaces.find(w => w.id === workspaceId);
  };

  // Helper function to get project by ID and workspace
  const getProjectById = (workspaceId, projectId) => {
    const projects = projectsData[workspaceId] || [];
    return projects.find(p => p.id === projectId);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        // Reset expanded states when closing
        setExpandedWorkspace(null);
        setExpandedProject(null);
        setExpandedJob(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Reset expanded states when opening
      setExpandedWorkspace(null);
      setExpandedProject(null);
      setExpandedJob(null);
      // Fetch entire navigation tree when opening dropdown
      fetchNavigationTree();
    }
  };

  return (
    <div className="workspace-tree-nav" ref={dropdownRef}>
      <button
        className="workspace-toggle-btn"
        onClick={toggleDropdown}
        title="Workspace Navigation"
      >
        <i className="fas fa-layer-group" />
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} toggle-icon`} />
      </button>

      {isOpen && (
        <div className="workspace-dropdown slide-in-left">
          <div className="workspace-header">
            <i className="fas fa-sitemap" />
            <span>Workspace Navigation</span>
          </div>
          
          <div className="workspace-tree-content">
            {loading ? (
              <div className="loading-state">
                <i className="fas fa-spinner fa-spin" />
                <span>Loading workspaces...</span>
              </div>
            ) : error ? (
              <div className="error-state">
                <i className="fas fa-exclamation-circle" />
                <span>{error}</span>
                <button onClick={fetchNavigationTree} className="retry-btn">
                  <i className="fas fa-redo" />
                  Retry
                </button>
              </div>
            ) : workspaces.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-building" />
                <span>No workspaces found</span>
              </div>
            ) : (
              workspaces.map((workspace) => {
                const workspaceProjects = projectsData[workspace.id] || [];
                
                return (
                  <div key={workspace.id} className="workspace-node">
                    <div
                      className="workspace-item"
                      onClick={() => handleWorkspaceClick(workspace.id)}
                      onDoubleClick={(e) => handleWorkspaceDoubleClick(e, workspace)}
                    >
                      <i className={`fas fa-chevron-${expandedWorkspace === workspace.id ? 'down' : 'right'} expand-icon`} />
                      <i className="fas fa-building workspace-icon" />
                      <div className="workspace-info">
                        <div className="workspace-name">{workspace.name}</div>
                      </div>
                      <span className="project-count">
                        ({workspace.projectCount || workspaceProjects.length})
                      </span>
                    </div>
                    
                    {expandedWorkspace === workspace.id && (
                      <div className="projects-container">
                        {workspaceProjects.length === 0 ? (
                          <div className="no-projects-message">
                            <i className="fas fa-project-diagram" />
                            <span>No projects available</span>
                          </div>
                        ) : (
                          workspaceProjects.map((project) => {
                            const projectJobs = jobsData[project.id] || [];
                            
                            return (
                              <div key={project.id} className="project-node">
                                <div
                                  className="project-item"
                                  onClick={() => handleProjectClick(project.id)}
                                  onDoubleClick={(e) => handleProjectDoubleClick(e, project)}
                                >
                                  <i className={`fas fa-chevron-${expandedProject === project.id ? 'down' : 'right'} expand-icon`} />
                                  <i className="fas fa-project-diagram project-icon" />
                                  <span className="project-name">{project.name}</span>
                                  <span className="job-count">
                                    ({project.jobCount || projectJobs.length})
                                  </span>
                                </div>
                                
                                {expandedProject === project.id && (
                                  <div className="jobs-container">
                                    {projectJobs.length === 0 ? (
                                      <div className="no-jobs-message">
                                        <i className="fas fa-tasks" />
                                        <span>No jobs available</span>
                                      </div>
                                    ) : (
                                      projectJobs.map((job) => {
                                        const jobTasks = tasksData[job.id] || [];
                                        
                                        return (
                                          <div key={job.id} className="job-node">
                                            <div
                                              className="job-item"
                                              onClick={() => handleJobClick(job.id)}
                                              onDoubleClick={(e) => handleJobDoubleClick(e, job)}
                                            >
                                              <i className={`fas fa-chevron-${expandedJob === job.id ? 'down' : 'right'} expand-icon`} />
                                              <i className="fas fa-tasks job-icon" />
                                              <span className="job-name">{job.name}</span>
                                              <span className="task-count">
                                                ({job.taskCount || jobTasks.length})
                                              </span>
                                            </div>
                                            
                                            {expandedJob === job.id && (
                                              <div className="tasks-container">
                                                {jobTasks.length === 0 ? (
                                                  <div className="no-tasks-message">
                                                    <i className="fas fa-check-square" />
                                                    <span>No tasks available</span>
                                                  </div>
                                                ) : (
                                                  // Tasks are already sorted by recency when fetched
                                                  jobTasks.map((task) => (
                                                    <div
                                                      key={task.id}
                                                      className="task-item"
                                                      onClick={() => handleTaskClick(task, job, project, workspace)}
                                                    >
                                                      <i className="fas fa-check-square task-icon" />
                                                      <span className="task-name">
                                                        {task.friendlyId ? `${task.friendlyId}: ${task.name}` : task.name}
                                                      </span>
                                                    </div>
                                                  ))
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkspaceTreeNav;